import React, { useState, useEffect } from 'react';
import { Gift, Clock, BookOpen, ArrowRight, Check, Mail, User, Phone, Sparkles, Youtube, Play, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Links dos cursos no YouTube
const YOUTUBE_COURSES = {
  1: { // Revit BootCamp
    playlistUrl: null, // Em atualização para versão 2026
    videoUrl: null,
    title: 'Revit BootCamp',
    description: 'Introdução completa ao Revit para iniciantes',
    isUpdating: true,
    updateMessage: 'Material sendo atualizado para a nova versão 2026'
  },
  2: { // BIM BootCamp
    playlistUrl: 'https://www.youtube.com/playlist?list=PLnuB2ffvbZnsS_UR5IyoCBgPaaL9yttLP',
    videoUrl: 'https://www.youtube.com/playlist?list=PLnuB2ffvbZnsS_UR5IyoCBgPaaL9yttLP',
    title: 'BIM BootCamp',
    description: 'Entenda os fundamentos do BIM e sua importância',
    isUpdating: false
  }
};

const FreeCoursesSection = () => {
  const [freeCourses, setFreeCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [leadForm, setLeadForm] = useState({
    name: '',
    email: '',
    phone: '',
    area: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchFreeCourses();
  }, []);

  const fetchFreeCourses = async () => {
    try {
      const response = await axios.get(`${API}/courses`);
      // Filter free courses (IDs 1, 2) - Design Review (ID 3) agora é bônus das formações
      const free = response.data.filter(c => [1, 2].includes(c.id));
      setFreeCourses(free);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleAccessClick = (course) => {
    setSelectedCourse(course);
    setShowLeadForm(true);
  };

  const handleAccessAllClick = () => {
    setSelectedCourse(null); // null significa todos os cursos
    setShowLeadForm(true);
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    
    if (!leadForm.name || !leadForm.email) {
      toast.error('Por favor, preencha nome e email');
      return;
    }

    setSubmitting(true);
    try {
      // Save lead to database
      await axios.post(`${API}/leads`, {
        name: leadForm.name,
        email: leadForm.email,
        phone: leadForm.phone,
        area: leadForm.area,
        source: 'free_youtube_course',
        course_interest: selectedCourse?.title || 'Todos os Cursos Gratuitos'
      });

      toast.success('Acesso liberado! Aproveite os cursos! 🎉');
      setShowLeadForm(false);
      setShowAccessModal(true);
      
    } catch (error) {
      // Mesmo com erro, libera acesso (melhor UX)
      setShowLeadForm(false);
      setShowAccessModal(true);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return null;

  return (
    <section className="py-16 bg-gradient-to-b from-black via-zinc-900 to-black">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Header */}
        <div className="text-center mb-12">
          <Badge className="bg-green-600 text-white mb-4 px-4 py-2 text-sm">
            <Gift className="w-4 h-4 mr-2 inline" />
            100% GRATUITO
          </Badge>
          <h2 className="text-4xl font-bold text-white mb-4">
            Comece Sua Jornada <span className="text-green-400">Sem Custo</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Oferecemos cursos totalmente gratuitos no YouTube para você conhecer nossa metodologia 
            e dar os primeiros passos no mundo BIM.
          </p>
        </div>

        {/* Free Courses Cards */}
        <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto mb-8">
          {freeCourses.map((course) => {
            const youtubeData = YOUTUBE_COURSES[course.id];
            const isUpdating = youtubeData?.isUpdating;
            
            return (
            <Card 
              key={course.id} 
              className={`bg-gradient-to-br from-zinc-900 to-zinc-800 border-2 ${isUpdating ? 'border-yellow-500/30 hover:border-yellow-500' : 'border-green-500/30 hover:border-green-500'} transition-all duration-300 overflow-hidden group relative`}
            >
              <div className={`absolute top-0 right-0 w-32 h-32 ${isUpdating ? 'bg-yellow-500/10 group-hover:bg-yellow-500/20' : 'bg-green-500/10 group-hover:bg-green-500/20'} rounded-full blur-3xl transition-all`} />
              
              <CardHeader className="pb-2 relative">
                <div className="flex justify-between items-start mb-2">
                  <Badge className="bg-green-600 text-white">
                    <Sparkles className="w-3 h-3 mr-1" />
                    GRÁTIS
                  </Badge>
                  {isUpdating ? (
                    <Badge className="bg-yellow-600 text-white">
                      Em breve
                    </Badge>
                  ) : (
                    <Badge className="bg-red-600 text-white">
                      <Youtube className="w-3 h-3 mr-1" />
                      YouTube
                    </Badge>
                  )}
                </div>
                <CardTitle className="text-xl text-white">{course.title}</CardTitle>
                <p className="text-sm text-gray-400 line-clamp-2">{course.description}</p>
                {isUpdating && (
                  <p className="text-xs text-yellow-400 mt-2">
                    {youtubeData.updateMessage}
                  </p>
                )}
              </CardHeader>

              <CardContent className="space-y-4">
                <div className="flex items-center text-sm text-gray-400">
                  <Clock size={14} className={isUpdating ? "text-yellow-500" : "text-green-500"} />
                  <span className="ml-2">{course.duration}</span>
                </div>

                {/* Syllabus Preview */}
                {course.syllabus && course.syllabus.length > 0 && (
                  <div className="bg-black/30 rounded-lg p-3">
                    <h4 className={`text-xs font-semibold ${isUpdating ? 'text-yellow-400' : 'text-green-400'} mb-2 flex items-center`}>
                      <BookOpen size={12} className="mr-1" />
                      O que você vai aprender:
                    </h4>
                    <div className="space-y-1">
                      {course.syllabus.slice(0, 4).map((item, index) => (
                        <div key={index} className="flex items-start space-x-2 text-xs text-gray-300">
                          <Check size={10} className={isUpdating ? "text-yellow-400" : "text-green-400"} />
                          <span className="line-clamp-1 mt-0.5">{item}</span>
                        </div>
                      ))}
                      {course.syllabus.length > 4 && (
                        <div className="text-xs text-gray-500 italic pl-4">
                          +{course.syllabus.length - 4} tópicos...
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Price */}
                <div className="text-center pt-2">
                  <div className="flex items-center justify-center space-x-2 mb-3">
                    <span className="text-gray-500 line-through text-lg">R$ 297,00</span>
                    <span className={`text-3xl font-bold ${isUpdating ? 'text-yellow-400' : 'text-green-400'}`}>R$ 0,00</span>
                  </div>
                  <Button
                    onClick={() => handleAccessClick(course)}
                    className={`w-full ${isUpdating ? 'bg-yellow-600 hover:bg-yellow-700' : 'bg-green-600 hover:bg-green-700'} text-white group`}
                    data-testid={`access-free-course-${course.id}`}
                  >
                    <Play className="mr-2 w-4 h-4" />
                    {isUpdating ? 'Cadastrar para Aviso' : 'Acessar Gratuitamente'}
                    <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )})}
        </div>

        {/* Access All Button */}
        <div className="text-center">
          <Button
            onClick={handleAccessAllClick}
            variant="outline"
            className="border-green-500 text-green-400 hover:bg-green-500/10"
            data-testid="access-all-free-courses"
          >
            <Gift className="mr-2 w-4 h-4" />
            Quero Acesso a Todos os Cursos Gratuitos
          </Button>
          <p className="text-gray-500 text-sm mt-4">
            Cadastre-se uma vez e tenha acesso a todos os cursos gratuitos no YouTube.
          </p>
        </div>
      </div>

      {/* Lead Capture Modal */}
      {showLeadForm && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-zinc-900 rounded-2xl p-8 max-w-md w-full border border-green-500/30 relative">
            <button
              onClick={() => setShowLeadForm(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white"
              data-testid="close-lead-form"
            >
              ✕
            </button>

            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Gift className="w-8 h-8 text-green-400" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">Acesso Gratuito</h3>
              <p className="text-gray-400">
                {selectedCourse 
                  ? <>Preencha seus dados para acessar o curso <strong className="text-green-400">{selectedCourse?.title}</strong></>
                  : <>Preencha seus dados para acessar <strong className="text-green-400">todos os cursos gratuitos</strong></>
                }
              </p>
            </div>

            <form onSubmit={handleLeadSubmit} className="space-y-4">
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Nome completo *</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    type="text"
                    placeholder="Seu nome"
                    value={leadForm.name}
                    onChange={(e) => setLeadForm({...leadForm, name: e.target.value})}
                    className="pl-10 bg-zinc-800 border-zinc-700 text-white"
                    required
                    data-testid="lead-name-input"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm text-gray-400 mb-1 block">Email *</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    type="email"
                    placeholder="seu@email.com"
                    value={leadForm.email}
                    onChange={(e) => setLeadForm({...leadForm, email: e.target.value})}
                    className="pl-10 bg-zinc-800 border-zinc-700 text-white"
                    required
                    data-testid="lead-email-input"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm text-gray-400 mb-1 block">WhatsApp</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    type="tel"
                    placeholder="(11) 99999-9999"
                    value={leadForm.phone}
                    onChange={(e) => setLeadForm({...leadForm, phone: e.target.value})}
                    className="pl-10 bg-zinc-800 border-zinc-700 text-white"
                    data-testid="lead-phone-input"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm text-gray-400 mb-1 block">Área de atuação</label>
                <select
                  value={leadForm.area}
                  onChange={(e) => setLeadForm({...leadForm, area: e.target.value})}
                  className="w-full bg-zinc-800 border border-zinc-700 text-white rounded-md px-3 py-2"
                  data-testid="lead-area-select"
                >
                  <option value="">Selecione...</option>
                  <option value="arquitetura">Arquitetura</option>
                  <option value="engenharia_civil">Engenharia Civil</option>
                  <option value="engenharia_eletrica">Engenharia Elétrica</option>
                  <option value="engenharia_mecanica">Engenharia Mecânica</option>
                  <option value="design_interiores">Design de Interiores</option>
                  <option value="construcao">Construção Civil</option>
                  <option value="estudante">Estudante</option>
                  <option value="outro">Outro</option>
                </select>
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-lg"
                data-testid="submit-lead-form"
              >
                {submitting ? 'Liberando acesso...' : 'Liberar Meu Acesso Gratuito'}
              </Button>

              <p className="text-xs text-gray-500 text-center">
                Ao continuar, você concorda em receber comunicações sobre nossos cursos e novidades BIM.
              </p>
            </form>
          </div>
        </div>
      )}

      {/* Access Modal - Shows YouTube Links */}
      {showAccessModal && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-zinc-900 rounded-2xl p-8 max-w-lg w-full border border-green-500/30 relative max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => {
                setShowAccessModal(false);
                setLeadForm({ name: '', email: '', phone: '', area: '' });
              }}
              className="absolute top-4 right-4 text-gray-400 hover:text-white"
              data-testid="close-access-modal"
            >
              ✕
            </button>

            <div className="text-center mb-6">
              <div className="w-20 h-20 bg-green-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-10 h-10 text-green-400" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">Acesso Liberado! 🎉</h3>
              <p className="text-gray-400">
                Olá <strong className="text-green-400">{leadForm.name.split(' ')[0]}</strong>! 
                Seu acesso aos cursos gratuitos está liberado.
              </p>
            </div>

            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-white flex items-center">
                <Youtube className="w-5 h-5 text-red-500 mr-2" />
                Seus Cursos Gratuitos:
              </h4>

              {/* Show selected course or all courses */}
              {(selectedCourse ? [selectedCourse] : freeCourses).map((course) => {
                const youtubeData = YOUTUBE_COURSES[course.id];
                const isUpdating = youtubeData?.isUpdating;
                
                return (
                  <div 
                    key={course.id}
                    className="bg-zinc-800 rounded-xl p-4 border border-zinc-700"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h5 className="font-semibold text-white">{course.title}</h5>
                        <p className="text-sm text-gray-400">{course.duration}</p>
                      </div>
                      {isUpdating ? (
                        <Badge className="bg-yellow-600 text-white text-xs">
                          Em breve
                        </Badge>
                      ) : (
                        <Badge className="bg-red-600 text-white text-xs">
                          <Youtube className="w-3 h-3 mr-1" />
                          YouTube
                        </Badge>
                      )}
                    </div>
                    
                    {isUpdating ? (
                      <div className="bg-yellow-600/10 border border-yellow-600/30 rounded-lg p-3 text-center">
                        <p className="text-yellow-400 text-sm font-medium mb-1">
                          Material em Atualização
                        </p>
                        <p className="text-gray-400 text-xs">
                          {youtubeData.updateMessage}
                        </p>
                        <p className="text-gray-500 text-xs mt-2">
                          Cadastre-se para ser avisado quando estiver disponível!
                        </p>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <a
                          href={youtubeData?.playlistUrl || '#'}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-lg py-2 px-4 text-sm font-medium flex items-center justify-center transition-colors"
                          data-testid={`youtube-playlist-${course.id}`}
                        >
                          <Play className="w-4 h-4 mr-2" />
                          Assistir Playlist
                        </a>
                        <a
                          href={youtubeData?.videoUrl || '#'}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-zinc-700 hover:bg-zinc-600 text-white rounded-lg py-2 px-4 text-sm font-medium flex items-center justify-center transition-colors"
                          data-testid={`youtube-video-${course.id}`}
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="mt-6 pt-6 border-t border-zinc-700">
              <p className="text-sm text-gray-400 text-center mb-4">
                Dica: Inscreva-se no canal <strong className="text-red-400">BIMTALKS</strong> para não perder novos conteúdos!
              </p>
              <a
                href="https://www.youtube.com/@Bimtalks?sub_confirmation=1"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full bg-red-600 hover:bg-red-700 text-white rounded-lg py-3 px-4 font-medium flex items-center justify-center transition-colors"
                data-testid="subscribe-youtube"
              >
                <Youtube className="w-5 h-5 mr-2" />
                Inscrever-se no Canal BIMTALKS
              </a>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default FreeCoursesSection;
